import { DevicesProvider, useEditor } from "@grapesjs/react";
import { DesktopMac, PhoneIphone, TabletMac, Code } from "@mui/icons-material";
import {
  AppBar,
  Backdrop,
  Badge,
  Button,
  ButtonGroup,
  Grid,
  IconButton,
  Slider,
  Stack,
  TextField,
  Toolbar,
} from "@mui/material";
import Breadcrumb from "Components/Breadcrumb/Breadcrumb";
import LightningLandersTopButtons from "features/lightningLanders/components/LightningLandersTopButtons";
import debounce from "lodash/debounce";
import { useState, useEffect, useCallback, useRef } from "react";
import { useDebounce } from "react-use";
import { customJsScript } from "features/lightningLanders/constants";
import {
  addOrUpdateScriptTag,
  getCanvasDocument,
} from "features/lightningLanders/utils";
import { useStylesUpdate } from "features/lightningLanders/providers/StylesUpdateProvider";
import LightningLanderBuilderCodeDrawer from "features/lightningLanders/components/LightningLanderBuilderCodeDrawer";
import NameAndDescriptionDialog from "Components/NameAndDescriptionDialog/NameAndDescriptionDialog";
import { useLightningLanderContext } from "features/lightningLanders/providers/LightningLanderProvider";
import {
  ApiAIPromptStatus,
  ApiComponentType,
  ApiPathStatus,
} from "@incendium/api";
import { saveComponent } from "features/campaigns";
import { useNotification } from "Hooks/useNotification";
import { useLanderTemplates } from "../hooks/useLanderTemplates";
import produce from "immer";
import { useSelectedProject } from "Hooks/useSelectedProject";
import Loading from "Components/Loading/Loading";
import { landersService } from "Apis";
import { useLanderComponents } from "../hooks/useLanderComponents";

const CANVAS_WIDTH_MIN = 280;
const CANVAS_WIDTH_MAX = 1200;

interface LightningLandgerBuilderTopBarProps {
  componentMode?: boolean;
  canvasWidth?: number;
  onCanvasWidthChange?: (width: number) => void;
  onPersistComponent?: () => Promise<void>;
}

function LightningLandgerBuilderTopBar({
  componentMode = false,
  canvasWidth = 400,
  onCanvasWidthChange,
  onPersistComponent,
}: LightningLandgerBuilderTopBarProps = {}) {
  const { selectedProject } = useSelectedProject();
  const { triggerUpdate } = useStylesUpdate();
  const { component, lander, setLander } = useLightningLanderContext();
  const { setComponents } = useLanderComponents();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [htmlContent, setHtmlContent] = useState(component?.html || "");
  const [cssContent, setCssContent] = useState(component?.css || "");
  const [jsContent, setJsContent] = useState(component?.js || "");
  const editor = useEditor();
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingComponent, setSavingComponent] = useState(false);
  const [activating, setActivating] = useState(false);
  const [unsavedChangesCount, setUnsavedChangesCount] = useState(0);
  const { setTemplates } = useLanderTemplates();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const saveInFlightRef = useRef(false);

  const isComponentMode =
    componentMode || component?.type === ApiComponentType.LANDING_COMPONENT;

  const updateAll = useCallback(() => {
    if (!editor || !editor.Components) return;
    setHtmlContent(editor.getHtml());
    setCssContent(
      editor.getCss({
        avoidProtected: true,
        keepUnusedStyles: true,
      }) || "",
    );
    const canvasDoc = getCanvasDocument(editor);
    if (canvasDoc) {
      const existingScript = canvasDoc.querySelector(
        `script[${customJsScript}]`,
      );
      const canvasJs = existingScript ? existingScript.textContent || "" : "";
      // Only update jsContent from canvas if script exists, or if component has no JS
      // This prevents overwriting with empty string before JS is added to canvas
      if (canvasJs || !component?.js) {
        setJsContent(canvasJs);
      }
    }
  }, [editor, component?.js]);

  const onUpdate = useCallback(() => {
    if (!editor || saving) return;
    editor.CssComposer.render();
    setTimeout(updateAll, 1);
  }, [editor, updateAll, saving]);

  // Initialize htmlContent, cssContent, jsContent from component on mount (only once)
  useEffect(() => {
    if (component?.html && htmlContent === "") {
      setHtmlContent(component.html);
    }
    if (component?.css && cssContent === "") {
      setCssContent(component.css);
    }
    if (component?.js && jsContent === "") {
      setJsContent(component.js);
    }
  }, [component]);

  // Update unsaved changes count using GrapesJS built-in getDirtyCount()
  const updateUnsavedChangesCount = useCallback(() => {
    if (!editor) return;
    const count = editor.getDirtyCount?.() || 0;
    setUnsavedChangesCount(count);
  }, [editor]);

  // Subscribe to editor changes to keep local state in sync with canvas.
  // Debounce so a single block drop (which fires component:add per nested component) triggers one sync instead of dozens.
  const SYNC_DEBOUNCE_MS = 150;
  useEffect(() => {
    if (!editor) return;

    const runSync = () => {
      onUpdate();
      updateUnsavedChangesCount();
    };
    const handleChange = debounce(runSync, SYNC_DEBOUNCE_MS);

    editor.on("component:add", handleChange);
    editor.on("component:remove", handleChange);
    editor.on("component:update", handleChange);
    editor.on("style:property:update", handleChange);
    editor.on("update", updateUnsavedChangesCount);

    editor.on("undo", updateUnsavedChangesCount);
    editor.on("redo", updateUnsavedChangesCount);

    onUpdate();
    updateUnsavedChangesCount();

    return () => {
      handleChange.cancel();
      editor.off("component:add", handleChange);
      editor.off("component:remove", handleChange);
      editor.off("component:update", handleChange);
      editor.off("style:property:update", handleChange);
      editor.off("update", updateUnsavedChangesCount);
      editor.off("undo", updateUnsavedChangesCount);
      editor.off("redo", updateUnsavedChangesCount);
    };
  }, [editor, onUpdate, updateUnsavedChangesCount]);

  // Also update when drawer is opened to ensure latest values
  useEffect(() => {
    if (drawerOpen && editor) {
      updateAll();
    }
  }, [drawerOpen, editor, updateAll]);

  const handleHtmlChange = useCallback((newHtml: string) => {
    setHtmlContent(newHtml);
  }, []);

  const handleCssChange = useCallback((newCss: string) => {
    setCssContent(newCss);
  }, []);

  const handleJsChange = (newJs: string) => {
    setJsContent(newJs);
  };

  const applyChanges = useCallback(() => {
    if (!editor) return;

    const normalizedHtml = (s: string) => s.replace(/\s{2,}/g, " ").trim();
    const currentHtml = normalizedHtml(editor.getHtml());
    const newHtml = normalizedHtml(htmlContent);
    const htmlChanged = currentHtml !== newHtml;

    const savedState = JSON.parse(JSON.stringify(editor.getProjectData()));

    // Stop, restore cleaned state, start, then apply so one undo step is recorded (undo = restored to savedState)
    editor.UndoManager.stop();
    try {
      editor.loadProjectData(JSON.parse(JSON.stringify(savedState)));
    } finally {
      editor.UndoManager.start();
    }
    try {
      editor.Css.clear();
      if (htmlChanged) {
        editor.setComponents(htmlContent.replace(/\s{2,}/g, " ").trim());
      }
      editor.setStyle(
        cssContent ||
          editor.getCss({
            keepUnusedStyles: true,
            avoidProtected: true,
          }),
      );
      const canvasDoc = getCanvasDocument(editor);
      if (canvasDoc) {
        addOrUpdateScriptTag(canvasDoc, customJsScript, jsContent);
      }
    } finally {
      editor.UndoManager.stop();
      editor.UndoManager.start();
    }

    setSaving(false);
    triggerUpdate();
    editor.select();
  }, [htmlContent, cssContent, jsContent, editor, triggerUpdate]);

  const onClose = useCallback(async () => {
    setSaving(true);
    setDrawerOpen(false);
  }, []);

  useDebounce(
    () => {
      if (saving && !drawerOpen) {
        applyChanges();
      }
    },
    100,
    [drawerOpen, saving, applyChanges],
  );

  const handleSaveAsTemplate = useCallback(
    async (name: string, description: string) => {
      if (!component || !selectedProject?.id) return;
      try {
        const res = await saveComponent(selectedProject.id, {
          ...component,
          id: undefined,
          name,
          description,
          path: undefined,
          type: ApiComponentType.LANDING_TEMPLATE,
        });
        showSuccessNotification(`Template (${name}) saved`);
        setTemplates((templates) =>
          produce(templates, (draft) => {
            draft.push(res.component!);
          }),
        );
      } catch (error) {
        showErrorNotification("Failed to save template, please try again");
      }
    },
    [component],
  );

  const handleSaveComponent = useCallback(async () => {
    if (!editor || !component || !onPersistComponent) return;
    if (saveInFlightRef.current) return;

    saveInFlightRef.current = true;
    setSavingComponent(true);
    try {
      await onPersistComponent();
      editor.clearDirtyCount();
      editor.UndoManager.clear();
      updateUnsavedChangesCount();
      showSuccessNotification("Component saved successfully");
    } catch (error) {
      showErrorNotification("Failed to save component, please try again");
    } finally {
      saveInFlightRef.current = false;
      setSavingComponent(false);
    }
  }, [
    editor,
    component,
    onPersistComponent,
    showSuccessNotification,
    showErrorNotification,
    updateUnsavedChangesCount,
  ]);

  const handleActivate = useCallback(async () => {
    if (!lander?.id || !selectedProject?.id) return;
    setActivating(true);
    try {
      await landersService.landersServiceActivateLander({
        projectId: selectedProject.id as number,
        landerId: lander.id as number,
      });
      const updatedLander = produce(lander, (draft) => {
        draft.status = ApiPathStatus.PATH_STATUS_ACTIVE;
      });
      setLander(updatedLander);

      // Also update the shared components list so the card view reflects the change
      setComponents((components) =>
        produce(components, (draft) => {
          if (!component?.id) {
            return;
          }
          const idx = draft.findIndex((d) => d.id === component.id);
          if (idx >= 0) {
            // Update the path property of the component
            draft[idx].path = updatedLander;
          }
        }),
      );

      showSuccessNotification(`${lander.path || "Lander"} Activated`);
    } catch (error) {
      showErrorNotification("Internal error, please try again");
    } finally {
      setActivating(false);
    }
  }, [
    lander,
    selectedProject,
    component,
    setLander,
    setComponents,
    showSuccessNotification,
    showErrorNotification,
  ]);

  return (
    <>
      <AppBar position="fixed" color={"transparent"} elevation={2}>
        <Toolbar variant="dense">
          <Grid container alignItems={"center"}>
            <Grid item xs={4}>
              <Breadcrumb />
            </Grid>
            <Grid
              item
              xs={isComponentMode ? 4 : 3}
              alignItems="center"
              justifyContent={"center"}
              display="flex"
            >
              {isComponentMode && onCanvasWidthChange ? (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ width: "100%", maxWidth: 320 }}
                >
                  <Slider
                    size="small"
                    value={canvasWidth}
                    onChange={(_, value) =>
                      onCanvasWidthChange(value as number)
                    }
                    min={CANVAS_WIDTH_MIN}
                    max={CANVAS_WIDTH_MAX}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v}px`}
                  />
                  <TextField
                    size="small"
                    type="number"
                    value={canvasWidth}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (!Number.isNaN(v))
                        onCanvasWidthChange(
                          Math.min(
                            CANVAS_WIDTH_MAX,
                            Math.max(CANVAS_WIDTH_MIN, v),
                          ),
                        );
                    }}
                    inputProps={{
                      min: CANVAS_WIDTH_MIN,
                      max: CANVAS_WIDTH_MAX,
                      step: 20,
                    }}
                    sx={{ width: 100 }}
                  />
                </Stack>
              ) : (
                <DevicesProvider>
                  {({ selected, select }) => (
                    <ButtonGroup>
                      <IconButton
                        onClick={() => select("desktop")}
                        color={selected === "desktop" ? "primary" : "default"}
                      >
                        <DesktopMac />
                      </IconButton>
                      <IconButton
                        onClick={() => select("tablet")}
                        color={selected === "tablet" ? "primary" : "default"}
                      >
                        <TabletMac />
                      </IconButton>
                      <IconButton
                        onClick={() => select("mobilePortrait")}
                        color={
                          selected === "mobilePortrait" ? "primary" : "default"
                        }
                      >
                        <PhoneIphone />
                      </IconButton>
                    </ButtonGroup>
                  )}
                </DevicesProvider>
              )}
            </Grid>
            <Grid
              item
              xs={isComponentMode ? 4 : 5}
              alignItems="center"
              justifyContent={"flex-end"}
              display="flex"
            >
              <IconButton
                onClick={() => setDrawerOpen(true)}
                color="primary"
                title="Edit CSS/HTML"
              >
                <Code />
              </IconButton>

              <LightningLandersTopButtons />

              <Stack direction="row" spacing={2} alignItems="center">
                <Badge
                  badgeContent={
                    unsavedChangesCount > 0 ? unsavedChangesCount : 0
                  }
                  color="primary"
                  max={99}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleSaveComponent}
                    disabled={
                      savingComponent ||
                      !editor ||
                      component?.prompt?.status ===
                        ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING
                    }
                  >
                    {savingComponent ? "Saving..." : "Save"}
                  </Button>
                </Badge>
                {component.type === ApiComponentType.LANDING && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setSaveAsTemplateOpen(true)}
                    >
                      Save Copy as Template
                    </Button>
                    {lander?.id &&
                    lander?.status !== ApiPathStatus.PATH_STATUS_ACTIVE ? (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleActivate}
                        disabled={activating || !lander?.id}
                      >
                        {activating ? "Activating..." : "Activate"}
                      </Button>
                    ) : undefined}
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>

      {saving ? (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={saving}
        >
          <Loading text="Applying Changes..." size={70} />
        </Backdrop>
      ) : (
        <LightningLanderBuilderCodeDrawer
          open={drawerOpen}
          onClose={onClose}
          htmlContent={htmlContent}
          cssContent={cssContent}
          jsContent={jsContent}
          onHtmlChange={handleHtmlChange}
          onCssChange={handleCssChange}
          onJsChange={handleJsChange}
        />
      )}

      <NameAndDescriptionDialog
        open={saveAsTemplateOpen}
        setOpen={setSaveAsTemplateOpen}
        title="Save as Template"
        onSaved={handleSaveAsTemplate}
        name={component?.name || "New Template"}
        description={component?.description || ""}
      />
    </>
  );
}

export default LightningLandgerBuilderTopBar;
